/**
 * DataEQ Connector Service
 *
 * Handles API calls for DataEQ connector operations.
 * Integrates with the metadata service via backend proxy.
 */

import { fetchWithAuth } from '../utils/api';
import type {
  ConnectorResponse,
  ConnectorCreateRequest,
  ConnectorUpdateRequest,
} from './types';

const DATAEQ_CONNECTOR_BASE_PATH = '/v1/connectors/dataeq';

/**
 * Get the current DataEQ connector configuration
 *
 * Returns connector status, organisation data from metadata service,
 * and associated dataset information.
 *
 * @returns Promise<ConnectorResponse>
 * @throws APIErrorException on error
 */
export const getDataEQConnector = async (): Promise<ConnectorResponse> => {
  return fetchWithAuth(DATAEQ_CONNECTOR_BASE_PATH);
};

/**
 * Create a new DataEQ connector
 *
 * Workflow:
 * 1. Generates Good Outcomes API key
 * 2. Creates organisation in metadata service with goKey
 * 3. Creates dataset in local database
 *
 * @param request - Connector creation request
 * @returns Promise<ConnectorResponse> - Includes goKey on creation
 * @throws APIErrorException on validation, conflict, or server errors
 */
export const createDataEQConnector = async (
  request: ConnectorCreateRequest
): Promise<ConnectorResponse> => {
  return fetchWithAuth(DATAEQ_CONNECTOR_BASE_PATH, {
    method: 'POST',
    body: JSON.stringify(request),
  });
};

/**
 * Update existing DataEQ connector
 *
 * Can update connector name and DataEQ accounts using PATCH semantics.
 * Organisation code is immutable and cannot be changed.
 *
 * @param request - Connector update request
 * @returns Promise<ConnectorResponse>
 * @throws APIErrorException on validation, not found, or server errors
 */
export const updateDataEQConnector = async (
  request: ConnectorUpdateRequest
): Promise<ConnectorResponse> => {
  return fetchWithAuth(DATAEQ_CONNECTOR_BASE_PATH, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
};

/**
 * Set DataEQ connector active status (enable/disable)
 *
 * Activates or deactivates the DataEQ connector.
 * When deactivated (active=false), soft-deletes the organisation in metadata service.
 * When activated (active=true), restores the organisation.
 *
 * @param active - True to activate, false to deactivate
 * @returns Promise with success status and message
 * @throws APIErrorException on not found or server errors
 */
export const setDataEQConnectorActive = async (
  active: boolean
): Promise<{ success: boolean; message: string; status: string }> => {
  return fetchWithAuth(`${DATAEQ_CONNECTOR_BASE_PATH}/active?active=${active}`, {
    method: 'PATCH',
  });
};

/**
 * DataEQ Connector Service object for easier imports
 */
export const dataeqConnectorService = {
  get: getDataEQConnector,
  create: createDataEQConnector,
  update: updateDataEQConnector,
  setActive: setDataEQConnectorActive,
};